import { z } from "zod";

import { ArrayBinaryOperator, TernaryOperator } from "../../operators.ts";
import type { IResolvableNumberArrayValue, IResolvableNumberValue } from "../../values.ts";
import { ResolvableNumberArrayValue, ResolvableNumberValue } from "../../values.ts";
import type { IArrayBinaryCondition, IArrayTernaryCondition } from "./common.ts";
import { arrayBinaryCondition, arrayTernaryCondition } from "./common.ts";

// Zod value schemas
const NumberArrayValue = z.union([z.array(z.number()), ResolvableNumberArrayValue]);
const NumberValue = z.union([z.number(), ResolvableNumberValue]);

// Zod schemas with type discriminator
export const NumberArrayBinaryCondition = arrayBinaryCondition(
  z.literal("numberArray"),
  ArrayBinaryOperator,
  NumberArrayValue,
  NumberValue,
);
export type NumberArrayBinaryCondition = z.infer<typeof NumberArrayBinaryCondition>;

export const NumberArrayTernaryCondition = arrayTernaryCondition(
  z.literal("numberArray"),
  TernaryOperator,
  NumberArrayValue,
  NumberValue,
);
export type NumberArrayTernaryCondition = z.infer<typeof NumberArrayTernaryCondition>;

export const NumberArrayCondition = z.union([NumberArrayBinaryCondition, NumberArrayTernaryCondition]);
export type NumberArrayCondition = z.infer<typeof NumberArrayCondition>;

// Interfaces for complex types (verified to match schemas)
export interface INumberArrayBinaryCondition extends IArrayBinaryCondition<
  number[] | IResolvableNumberArrayValue,
  number | IResolvableNumberValue
> {
  type: "numberArray";
}

export interface INumberArrayTernaryCondition extends IArrayTernaryCondition<
  number[] | IResolvableNumberArrayValue,
  number | IResolvableNumberValue
> {
  type: "numberArray";
}

// Type compatibility assertions - compile-time check that inferred types match interfaces
type _AssertBinaryCompatible = NumberArrayBinaryCondition extends INumberArrayBinaryCondition ? true : never;
type _AssertTernaryCompatible = NumberArrayTernaryCondition extends INumberArrayTernaryCondition ? true : never;
const _binaryCheck: _AssertBinaryCompatible = true;
const _ternaryCheck: _AssertTernaryCompatible = true;
void _binaryCheck;
void _ternaryCheck;
